import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useSession } from '@/lib/SessionContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Lock, Save, Loader2, Upload, Trash, CheckCircle2, Sparkles, ShieldAlert
} from 'lucide-react';
import { canEditCompanySettings, checkIsDev, checkIsOwner, getPageAccess } from '@/lib/AuthSession';

// Subcomponent for Company Settings Image Upload
const AssetUploadCard = ({ label, description, currentUrl, onUpload, onRemove, uploading, readOnly }) => {
  const fileRef = useRef();

  return (
    <div className="border border-[#E6D5B8]/60 rounded-xl p-4 space-y-3 bg-[#FFFDF9]">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-bold text-xs text-[#302621]">{label}</p>
          <p className="text-[10px] text-[#7A695B]">{description}</p>
        </div>
        {currentUrl && !readOnly && (
          <Button variant="ghost" size="icon" className="w-8 h-8 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={onRemove}>
            <Trash className="w-4 h-4" />
          </Button>
        )}
      </div>

      {currentUrl ? (
        <div className="relative bg-[#F9F5EC] border border-[#E6D5B8]/30 rounded-lg p-3 flex items-center justify-center min-h-[100px]">
          <img src={currentUrl} alt={label} className="max-h-[100px] max-w-full object-contain" />
          <div className="absolute top-2 right-2">
            <span className="bg-emerald-100 text-emerald-800 text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 font-bold">
              <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Tersimpan
            </span>
          </div>
        </div>
      ) : (
        <div
          className={`border-2 border-dashed border-[#E6D5B8] rounded-lg p-6 flex flex-col items-center justify-center gap-2 bg-[#FFFDF9]/50 ${readOnly ? 'opacity-50' : 'cursor-pointer hover:bg-[#F9F5EC]/50 transition-colors'}`}
          onClick={() => !readOnly && fileRef.current?.click()}
        >
          <Sparkles className="w-6 h-6 text-[#A39171]" />
          <p className="text-[10px] text-[#7A695B] text-center">
            {readOnly ? 'Tidak ada gambar' : 'Klik untuk unggah gambar\n(PNG, JPG, WebP — maks 5MB)'}
          </p>
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && onUpload) onUpload(file);
          e.target.value = '';
        }}
      />

      {!readOnly && (
        <Button
          variant="outline"
          size="sm"
          className="w-full gap-2 text-xs border-[#E6D5B8] text-[#8C6208] hover:bg-[#F9F5EC] rounded-lg h-8"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {uploading ? 'Mengunggah...' : currentUrl ? 'Ganti Gambar' : 'Unggah Gambar'}
        </Button>
      )}
    </div>
  );
};

export default function WorkspaceSettings() {
  const { member: currentMember } = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Guard access based on page_access config
  useEffect(() => {
    if (!currentMember) return;
    const access = getPageAccess(currentMember, 'pengaturan');
    const isAdminOrPrivileged = checkIsDev(currentMember) || checkIsOwner(currentMember) || currentMember.role === 'admin';
    if (access === 'none' && !isAdminOrPrivileged) {
      navigate('/office');
    }
  }, [currentMember, navigate]);

  const [uploadingField, setUploadingField] = useState(null);
  const [settingsForm, setSettingsForm] = useState({
    nama_perusahaan: '',
    alamat_perusahaan: '',
    nama_direktur: '',
    nama_pengirim_sj: '',
    kota: '',
    portal_status: {
      landing: 'normal',
      production: 'normal',
      finance: 'normal',
      workspace: 'normal',
      marketing: 'normal',
      custom_message: ''
    }
  });

  // Fetch Company Settings
  const { data: settingsList = [], isLoading: isLoadingSettings } = useQuery({
    queryKey: ['company-settings'],
    queryFn: () => base44.entities.CompanySettings.list(),
  });

  const companySettings = settingsList[0] || {};

  useEffect(() => {
    if (settingsList[0]) {
      setSettingsForm({
        nama_perusahaan: settingsList[0].nama_perusahaan || '',
        alamat_perusahaan: settingsList[0].alamat_perusahaan || '',
        nama_direktur: settingsList[0].nama_direktur || '',
        nama_pengirim_sj: settingsList[0].nama_pengirim_sj || '',
        kota: settingsList[0].kota || '',
        portal_status: settingsList[0].portal_status || {
          landing: 'normal',
          production: 'normal',
          finance: 'normal',
          workspace: 'normal',
          marketing: 'normal',
          custom_message: ''
        }
      });
    }
  }, [settingsList]);

  const canEditSettings = canEditCompanySettings(currentMember);

  // Settings Mutation — with optimistic update for instant save feel
  const saveSettingsMutation = useMutation({
    mutationFn: async (data) => {
      if (companySettings.id) {
        return base44.entities.CompanySettings.update(companySettings.id, data);
      } else {
        return base44.entities.CompanySettings.create(data);
      }
    },
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: ['company-settings'] });
      const prev = queryClient.getQueryData(['company-settings']);
      queryClient.setQueryData(['company-settings'], (old = []) => {
        if (old.length > 0) {
          return [{ ...old[0], ...data, updated_date: new Date().toISOString() }];
        }
        return [{ ...data, id: 'temp_' + Date.now(), created_date: new Date().toISOString(), updated_date: new Date().toISOString() }];
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['company-settings'], ctx.prev);
      toast.error('Gagal menyimpan pengaturan.');
    },
    onSuccess: () => {
      toast.success('Identitas perusahaan berhasil diperbarui.');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['company-settings'] });
    }
  });

  // Company Settings Upload handles
  const handleUpload = async (file, field) => {
    setUploadingField(field);
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      if (companySettings.id) {
        await base44.entities.CompanySettings.update(companySettings.id, { [field]: file_url });
      } else {
        await base44.entities.CompanySettings.create({ [field]: file_url });
      }
      queryClient.invalidateQueries({ queryKey: ['company-settings'] });
      toast.success(`Berkas gambar berhasil diunggah.`);
    } catch(err) {
      toast.error('Gagal mengunggah gambar.');
    } finally {
      setUploadingField(null);
    }
  };

  const handleRemoveSettingImg = async (field) => {
    if (!companySettings.id) return;
    await base44.entities.CompanySettings.update(companySettings.id, { [field]: null });
    queryClient.invalidateQueries({ queryKey: ['company-settings'] });
    toast.success('Gambar berhasil dihapus.');
  };

  return (
    <div className="space-y-6 bg-white border border-[#E6D5B8] p-6 rounded-3xl shadow-xs max-w-4xl mx-auto text-[#302621]">
      
      {/* Header Pengaturan */}
      <div className="flex justify-between items-center border-b border-slate-100 pb-5">
        <div>
          <h1 className="text-xl font-black text-[#302621] tracking-tight">Profil & Pengaturan Perusahaan</h1>
          <p className="text-xs text-[#7A695B] mt-0.5">Sesuaikan kop surat, tanda tangan direktur, stempel otomatis invoice, dan legalitas pabrik.</p>
        </div>
        {!canEditSettings && (
          <span className="flex items-center gap-1.5 text-xs text-[#7A695B] bg-[#F9F5EC] px-3 py-1.5 rounded-full border border-[#E6D5B8]/60 font-bold">
            <Lock className="w-3.5 h-3.5 text-[#8C6208]" /> Hanya Lihat
          </span>
        )}
      </div>

      {/* Form */}
      <div className="space-y-6">
        
        {/* Text Inputs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] font-black text-[#8C6208] uppercase tracking-wider block mb-1.5">Nama Perusahaan</label>
            <Input 
              value={settingsForm.nama_perusahaan}
              onChange={e => setSettingsForm(f => ({ ...f, nama_perusahaan: e.target.value }))}
              placeholder="PT. Satala Dermatech Essential"
              disabled={!canEditSettings}
              className="h-10 text-xs border-[#E6D5B8] bg-[#FFFDF9] focus-visible:ring-[#8C6208]"
            />
          </div>
          <div>
            <label className="text-[10px] font-black text-[#8C6208] uppercase tracking-wider block mb-1.5">Kota Kantor (Untuk Penandatanganan)</label>
            <Input 
              value={settingsForm.kota}
              onChange={e => setSettingsForm(f => ({ ...f, kota: e.target.value }))}
              placeholder="Bogor"
              disabled={!canEditSettings}
              className="h-10 text-xs border-[#E6D5B8] bg-[#FFFDF9] focus-visible:ring-[#8C6208]"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] font-black text-[#8C6208] uppercase tracking-wider block mb-1.5">Alamat Lengkap Perusahaan</label>
            <Input 
              value={settingsForm.alamat_perusahaan}
              onChange={e => setSettingsForm(f => ({ ...f, alamat_perusahaan: e.target.value }))}
              placeholder="Jalan aster kavling Paspampres, Kota Batu, Kec. Ciomas, Bogor"
              disabled={!canEditSettings}
              className="h-10 text-xs border-[#E6D5B8] bg-[#FFFDF9] focus-visible:ring-[#8C6208]"
            />
          </div>
          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-black text-[#8C6208] uppercase tracking-wider block mb-1.5">Nama Direktur Utama (Untuk Invoice)</label>
              <Input 
                value={settingsForm.nama_direktur}
                onChange={e => setSettingsForm(f => ({ ...f, nama_direktur: e.target.value }))}
                placeholder="Umar Syarif"
                disabled={!canEditSettings}
                className="h-10 text-xs border-[#E6D5B8] bg-[#FFFDF9] focus-visible:ring-[#8C6208]"
              />
            </div>
            <div>
              <label className="text-[10px] font-black text-[#8C6208] uppercase tracking-wider block mb-1.5">Nama Pengirim Surat Jalan</label>
              <Input 
                value={settingsForm.nama_pengirim_sj}
                onChange={e => setSettingsForm(f => ({ ...f, nama_pengirim_sj: e.target.value }))}
                placeholder="Ahmad Fauzi"
                disabled={!canEditSettings}
                className="h-10 text-xs border-[#E6D5B8] bg-[#FFFDF9] focus-visible:ring-[#8C6208]"
              />
            </div>
          </div>
        </div>

        {canEditSettings && (
          <Button 
            onClick={() => saveSettingsMutation.mutate(settingsForm)}
            className="bg-[#302621] hover:bg-slate-800 text-white font-bold h-10 text-xs gap-1.5 shadow-sm rounded-xl"
            disabled={saveSettingsMutation.isPending}
          >
            {saveSettingsMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-4 h-4" />}
            Simpan Profil Perusahaan
          </Button>
        )}

        <hr className="border-slate-100" />

        {/* Portal Status Management (Hanya Developer & Owner) */}
        {(checkIsDev(currentMember) || checkIsOwner(currentMember)) && (
          <div className="space-y-4 bg-[#F9F5EC] border border-[#E6D5B8] p-5 rounded-2xl">
            <div>
              <h3 className="text-sm font-black text-[#8C6208] flex items-center gap-2">
                <ShieldAlert className="w-4 h-4" />
                Manajemen Akses & Status Portal
              </h3>
              <p className="text-xs text-[#7A695B] mt-1">Atur mode Maintenance atau Libur untuk masing-masing portal. User biasa akan diblokir saat mode ini aktif.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { key: 'landing', label: 'Main Page / Klien' },
                { key: 'production', label: 'Portal Produksi' },
                { key: 'finance', label: 'Portal Finance' },
                { key: 'workspace', label: 'Portal Workspace' },
                { key: 'marketing', label: 'Portal Marketing' }
              ].map(portal => (
                <div key={portal.key} className="bg-white p-3 rounded-xl border border-[#E6D5B8]/50 shadow-sm">
                  <label className="text-[10px] font-bold text-[#4A3B32] uppercase block mb-2">{portal.label}</label>
                  <select
                    disabled={!canEditSettings}
                    value={settingsForm.portal_status[portal.key]}
                    onChange={e => setSettingsForm(f => ({
                      ...f,
                      portal_status: { ...f.portal_status, [portal.key]: e.target.value }
                    }))}
                    className={`w-full h-8 text-xs font-semibold rounded-lg border outline-none px-2 ${
                      settingsForm.portal_status[portal.key] === 'normal' 
                        ? 'border-[#E6D5B8] text-[#302621] bg-white' 
                        : settingsForm.portal_status[portal.key] === 'holiday'
                          ? 'border-amber-300 bg-amber-50 text-amber-700'
                          : 'border-red-300 bg-red-50 text-red-700'
                    }`}
                  >
                    <option value="normal">✅ Normal Aktif</option>
                    <option value="maintenance">🛠️ Perbaikan (Maintenance)</option>
                    <option value="holiday">🏖️ Libur Pabrik (Holiday)</option>
                  </select>
                </div>
              ))}
            </div>

            <div className="mt-4">
              <label className="text-[10px] font-black text-[#8C6208] uppercase tracking-wider block mb-1.5">Pesan Tampilan Kustom (Opsional)</label>
              <Input 
                value={settingsForm.portal_status.custom_message || ''}
                onChange={e => setSettingsForm(f => ({
                  ...f,
                  portal_status: { ...f.portal_status, custom_message: e.target.value }
                }))}
                placeholder="Contoh: Kami libur Lebaran dan akan kembali beroperasi tanggal 15 Mei 2026."
                disabled={!canEditSettings}
                className="h-10 text-xs border-[#E6D5B8] bg-white focus-visible:ring-[#8C6208]"
              />
            </div>
            
            {canEditSettings && (
              <div className="pt-2">
                <Button 
                  onClick={() => saveSettingsMutation.mutate(settingsForm)}
                  className="bg-[#8C6208] hover:bg-[#6A4A06] text-white font-bold h-9 text-xs gap-1.5 shadow-sm rounded-lg"
                  disabled={saveSettingsMutation.isPending}
                >
                  {saveSettingsMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  Simpan Status Portal
                </Button>
              </div>
            )}
          </div>
        )}

      </div>

    </div>
  );
}
